import { motion } from "framer-motion";
import { Sparkles, Rocket, Target, TrendingUp } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const BrandifyAbout = () => {
  return (
    <div className="min-h-screen bg-[#030303] brandify-body">
      <SEO brand="brandify" path="/brandify/about" title="About" description="The agency behind data-driven, AI-first brand strategies that scale." />
      <Navigation brand="brandify" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <Sparkles className="w-20 h-20 text-[#1cb5ae] mx-auto mb-8" />
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="about-heading">
              AI-DRIVEN MARKETING.
              <br />
              <span className="text-[#1cb5ae]">REAL-WORLD GROWTH.</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Brandify Official is a next-generation marketing agency that combines artificial intelligence, strategic thinking, and custom technology to accelerate brand growth at unprecedented speed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
                WHO WE ARE
              </h2>
              <p className="text-lg text-gray-300 mb-4">
                We're not your typical marketing agency. We're a team of strategists, technologists, and creative thinkers who believe that AI isn't the future—it's the present.
              </p>
              <p className="text-lg text-gray-300 mb-4">
                While others are still debating whether to adopt AI, we've already built our entire operation around it. This means faster campaigns, smarter targeting, and results that scale.
              </p>
              <p className="text-lg text-gray-300">
                But technology alone isn't enough. We combine AI efficiency with human creativity to build brands that don't just grow—they dominate their categories.
              </p>
            </div>
            <div className="glass-effect rounded-2xl p-8">
              <img
                src="https://images.unsplash.com/photo-1764486097460-2650e1149760?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="AI Technology"
                className="w-full h-80 object-cover rounded-xl mb-6"
              />
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-[#1cb5ae] mb-2">50+</div>
                  <div className="text-sm text-gray-400">Brands Scaled</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#1cb5ae] mb-2">2.5x</div>
                  <div className="text-sm text-gray-400">Avg. Growth</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#1cb5ae] mb-2">24/7</div>
                  <div className="text-sm text-gray-400">AI Optimization</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="heading-font text-4xl md:text-5xl text-white text-center mb-16"
          >
            OUR PHILOSOPHY
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Rocket className="w-12 h-12 text-[#1cb5ae]" />,
                title: "Speed is Strategy",
                description: "In today's market, the fastest mover wins. Our AI-powered workflows let us launch campaigns in days, not weeks."
              },
              {
                icon: <Target className="w-12 h-12 text-[#8B5CF6]" />,
                title: "Data-Driven Decisions",
                description: "Every decision is backed by data. We don't guess—we analyze, test, and optimize based on real performance metrics."
              },
              {
                icon: <TrendingUp className="w-12 h-12 text-[#06B6D4]" />,
                title: "Growth at Scale",
                description: "We build systems that scale. Whether you're a startup or enterprise, our strategies grow with your ambitions."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-2xl p-8 hover:bg-white/10 transition-all"
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="heading-font text-2xl text-white mb-3">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
              READY TO ACCELERATE YOUR GROWTH?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's build something extraordinary together.
            </p>
            <Link
              to="/brandify/contact"
              className="inline-block px-8 py-4 bg-[#1cb5ae] text-white rounded-full font-medium hover:bg-[#1aada5] transition-colors"
              data-testid="contact-cta-btn"
            >
              Get Started
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer brand="brandify" />
    </div>
  );
};

export default BrandifyAbout;